import type { Identifier } from "ra-core";

import type { RequirementsStatus } from "./requirements";

/** Standard installation requirements (gula_clients columns). */
export type GulaRequirements = {
  req_photos?: string | null;
  req_drain?: string | null;
  req_space?: string | null;
  req_power?: string | null;
  req_elevator?: string | null;
  req_notes?: string | null;
  req_approved_at?: string | null;
  requirements_status?: RequirementsStatus;
};

/** A second delivery address received from Stripe, pending confirmation. */
export type AddressAlert = {
  address: string;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
  name?: string | null;
  source?: string | null;
  seen_at?: string | null;
};

export type GulaClient = GulaRequirements & {
  id: Identifier;
  contact_id: Identifier;
  stage: string;
  stage_changed_at: string;
  recontact_at?: string | null;
  lost_reason?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
  zone?: string | null;
  installer_id?: Identifier | null;
  sales_id?: Identifier | null;
  call_slot?: string | null;
  call_attempts?: number;
  last_call_attempt_at?: string | null;
  source?: string | null;
  campaign_name?: string | null;
  adset_name?: string | null;
  ad_name?: string | null;
  form_name?: string | null;
  external_lead_id?: string | null;
  lead_answer?: string | null;
  water_source?: string | null;
  price_fit?: string | null;
  install_timing?: string | null;
  lead_temperature?: string | null;
  stripe_customer_id?: string | null;
  address_alert?: AddressAlert | null;
  created_at: string;
  // Pipeline hygiene (who had the lead, and its clocks)
  owner_since?: string | null;
  keep_until?: string | null;
  released_from?: Identifier | null;
  released_at?: string | null;
  release_reason?: string | null;
  reactivation_at?: string | null;
  /** Last call, note, WhatsApp sent or stage change (gula_clients_summary) */
  last_activity_at?: string | null;
  // gula_clients_summary columns
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  installer_name?: string | null;
  nb_photo_notes?: number;
  order_stage?: string | null;
  maintenance_status?: string | null;
  maintenance_interval?: string | null;
  rental_status?: string | null;
  total_paid?: number | null;
  req_approved_by_name?: string | null;
};

export type GulaSubscription = {
  id: Identifier;
  contact_id: Identifier;
  stripe_subscription_id: string;
  stripe_customer_id?: string | null;
  plan_name?: string | null;
  kind: string;
  billing_interval?: string | null;
  amount?: number | null;
  status: string;
  started_at?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end: boolean;
  canceled_at?: string | null;
};

export type GulaPayment = {
  id: Identifier;
  contact_id: Identifier;
  stripe_id: string;
  kind: string;
  description?: string | null;
  amount: number;
  status: string;
  paid_at?: string | null;
  receipt_url?: string | null;
  created_at?: string;
};

export type GulaOrder = GulaRequirements & {
  id: Identifier;
  client_id: Identifier;
  contact_id: Identifier;
  stage: string;
  stage_changed_at: string;
  /** Team member credited with the sale (ranking) */
  sold_by?: Identifier | null;
  /** Owner of the lead */
  client_sales_id?: Identifier | null;
  /** When the customer got the tracking email */
  tracking_emailed_at?: string | null;
  product: string;
  plan?: string | null;
  price?: number | null;
  payment_method?: string | null;
  paid_at?: string | null;
  tracking_number?: string | null;
  shipped_at?: string | null;
  received_at?: string | null;
  /** Who confirmed the delivery: packlink (carrier tracking), whatsapp, phone */
  received_source?: string | null;
  /** YYYY-MM-DD the carrier should deliver (informative; the parte waits for the delivery) */
  expected_delivery_date?: string | null;
  /** Where the parcel is per Packlink + carrier: not_shipped, in_transit, out_for_delivery, delivered, incident, returned */
  delivery_status?: string | null;
  /** Last carrier tracking event, its time and city */
  delivery_event?: string | null;
  delivery_event_at?: string | null;
  delivery_event_city?: string | null;
  /** Last time the shipment was checked in Packlink */
  delivery_checked_at?: string | null;
  installer_id?: Identifier | null;
  installer_notified_at?: string | null;
  installation_date?: string | null;
  installed_at?: string | null;
  notes?: string | null;
  shipping_cost?: number | null;
  shipping_carrier?: string | null;
  packlink_reference?: string | null;
  packlink_service?: string | null;
  packlink_state?: string | null;
  packlink_label_url?: string | null;
  packlink_tracking_url?: string | null;
  packlink_updated_at?: string | null;
  created_at: string;
  // gula_orders_summary columns
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
  zone?: string | null;
  address_alert?: AddressAlert | null;
  installer_name?: string | null;
  installer_email?: string | null;
};

export type GulaInstaller = {
  id: Identifier;
  name: string;
  email?: string | null;
  phone?: string | null;
  zone_label: string;
  postal_prefixes: string[];
  is_default: boolean;
};

export const fullName = (record: { first_name?: string; last_name?: string }) =>
  [record.first_name, record.last_name].filter(Boolean).join(" ") ||
  "Sin nombre";
